/**
 * Turns the debate's Firestore steps into a social-style thread: each expert's
 * opening argument is a comment, the cross-examination and Chaos Agent attacks
 * aimed at that expert are replies under it, and the revision replaces the
 * comment's text as an edit.
 *
 * Debates written since the backend started emitting `targets` are routed
 * exactly. Older debates (saved history) fall back to splitting the text on
 * headings that name a panel member.
 */
import { expertLabel } from "./debateStages";
import { Lean } from "./verdict";

export type ReplyKind = "feedback" | "chaos" | "fact_check";

export interface ThreadReply {
  id: string;
  /** Expert key, or "chaos" / "fact_checker". */
  author: string;
  kind: ReplyKind;
  round: number;
  text: string;
  streaming: boolean;
  /** Addressed to the whole panel rather than this thread's expert. */
  toPanel: boolean;
  /** Set only where the whole step is this one reply; a split attack has no per-reply sources. */
  sourceIds?: string[];
}

export interface ThreadComment {
  expert: string;
  original: string;
  originalStreaming: boolean;
  /** Null until the expert starts revising. */
  revised: string | null;
  revisedStreaming: boolean;
  researching: boolean;
  replies: ThreadReply[];
  /** Authors whose reply to this expert has started but has no text for it yet. */
  replying: string[];
  model: string | null;
  /** Stated leans on questions with fixed answers; null until the step finishes. */
  openingLean: Lean | null;
  revisedLean: Lean | null;
  /** Sources each version of the argument relied on. */
  openingSourceIds: string[];
  revisedSourceIds: string[];
}

export interface ContextNote {
  key: string;
  author: string;
  title: string;
  text: string;
  streaming: boolean;
  sourceIds?: string[];
}

export interface DebateThread {
  /** Framing and research shown under the question (single-expert analysis). */
  context: ContextNote[];
  comments: ThreadComment[];
  /** Chaos Agent remarks that named nobody on the panel. */
  panelReplies: ThreadReply[];
  synthesis: { text: string; streaming: boolean } | null;
  /** True while the synthesis has not started but will appear. */
  synthesisPending: boolean;
  replyCount: number;
}

export interface ThreadInput {
  councilProgress: any[];
  chaosProgress: any[];
  councilFinal: string | null;
  facilitatorDone: boolean;
  isLoading: boolean;
  selectedExperts: string[];
  debateMode: "single" | "multi";
}

const PLACEHOLDERS = /^\((no response|no revision|no critique)\)$/;

const textOf = (step: any): string => {
  const text: string = step?.answer || step?.feedback || step?.revised || "";
  return PLACEHOLDERS.test(text.trim()) ? "" : text;
};

function normalise(value: string): string {
  return value
    .toLowerCase()
    .replace(/\b(the|expert|agent)\b/g, " ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function resolveMember(raw: string, members: string[]): string | null {
  const wanted = normalise(raw);
  if (!wanted) return null;
  const exact = members.find((m) => normalise(m) === wanted || normalise(expertLabel(m)) === wanted);
  if (exact) return exact;
  if (wanted.length < 3) return null;
  const partial = members.filter((m) => {
    const key = normalise(m);
    const label = normalise(expertLabel(m));
    return (
      key.includes(wanted) ||
      label.includes(wanted) ||
      (key.length >= 4 && wanted.includes(key)) ||
      (label.length >= 4 && wanted.includes(label))
    );
  });
  return partial.length === 1 ? partial[0] : null;
}

/**
 * Split legacy text on heading or bold-only lines that name one panel member,
 * e.g. "## Chaos Attack: Economist", "### To Statistician", "**Economist:**".
 */
export function splitByMemberHeadings(
  text: string,
  members: string[],
): { targets: Record<string, string>; toPanel: string } {
  const targets: Record<string, string> = {};
  const panel: string[] = [];
  let current: string | null = null;
  let buffer: string[] = [];
  const commit = () => {
    const body = buffer.join("\n").trim();
    if (body) {
      if (current) targets[current] = targets[current] ? `${targets[current]}\n\n${body}` : body;
      else panel.push(body);
    }
    buffer = [];
  };
  for (const line of text.split("\n")) {
    const heading =
      line.match(/^\s*#{1,4}\s+(.+?)\s*:?\s*$/) || line.match(/^\s*\*\*([^*]+)\*\*\s*:?\s*$/);
    const name = heading?.[1]
      .replace(/^chaos attack\s*:\s*/i, "")
      .replace(/^(response to|reply to|to|re)\b\s*:?\s*/i, "")
      .replace(/^@/, "")
      .replace(/[[\]:]/g, "");
    const member = name ? resolveMember(name, members) : null;
    if (member) {
      commit();
      current = member;
    } else {
      buffer.push(line);
    }
  }
  commit();
  return { targets, toPanel: panel.join("\n\n") };
}

function repliesFrom(
  step: any,
  text: string,
  members: string[],
): { targets: Record<string, string>; toPanel: string } {
  if (step.targets && typeof step.targets === "object") {
    return { targets: step.targets, toPanel: step.toPanel || "" };
  }
  return splitByMemberHeadings(text, members);
}

function leanOf(step: any): Lean | null {
  const lean = step?.lean;
  return lean && typeof lean === "object" && Object.keys(lean).length > 0 ? lean : null;
}

function sourceIdsOf(step: any): string[] {
  return Array.isArray(step?.sourceIds) ? step.sourceIds : [];
}

function latestModel(steps: any[]): string | null {
  for (let i = steps.length - 1; i >= 0; i--) if (steps[i].model) return steps[i].model;
  return null;
}

function buildMulti(input: ThreadInput): DebateThread {
  const { councilProgress, chaosProgress, isLoading, selectedExperts } = input;
  const members = selectedExperts;
  const live = (step: any) => isLoading && step?.done !== true;

  const comments: ThreadComment[] = members.map((expert) => {
    const mine = councilProgress.filter((p) => p.expert === expert);
    const initial = mine.find((p) => p.step === "initial");
    const revisions = mine.filter((p) => p.step === "revision").sort((a, b) => (a.round ?? 0) - (b.round ?? 0));
    const revision = revisions[revisions.length - 1];
    const gather = mine.find((p) => p.step === "research_gather");
    return {
      expert,
      original: textOf(initial),
      originalStreaming: !!initial && live(initial),
      revised: revision ? textOf(revision) : null,
      revisedStreaming: !!revision && live(revision),
      researching: !!gather && !textOf(initial) && isLoading,
      replies: [],
      replying: [],
      model: latestModel(mine),
      openingLean: leanOf(initial),
      revisedLean: leanOf(revision),
      openingSourceIds: sourceIdsOf(initial),
      revisedSourceIds: sourceIdsOf(revision),
    };
  });
  const byExpert = new Map(comments.map((c) => [c.expert, c]));
  const panelReplies: ThreadReply[] = [];

  const feedbackSteps = councilProgress
    .filter((p) => p.step === "feedback" && members.includes(p.expert))
    .sort((a, b) => (a.round ?? 0) - (b.round ?? 0) || members.indexOf(a.expert) - members.indexOf(b.expert));
  for (const step of feedbackSteps) {
    const round = step.round ?? 1;
    const streaming = live(step);
    const { targets, toPanel } = repliesFrom(step, textOf(step), members);
    for (const target of members) {
      if (target === step.expert) continue;
      const comment = byExpert.get(target)!;
      const text = targets[target];
      if (text) {
        comment.replies.push({
          id: `feedback-${round}-${step.expert}-${target}`,
          author: step.expert,
          kind: "feedback",
          round,
          text,
          streaming,
          toPanel: false,
        });
      } else if (streaming) {
        comment.replying.push(step.expert);
      }
    }
    if (toPanel) {
      byExpert.get(step.expert)?.replies.push({
        id: `feedback-${round}-${step.expert}-panel`,
        author: step.expert,
        kind: "feedback",
        round,
        text: toPanel,
        streaming,
        toPanel: true,
      });
    }
  }

  for (const chaos of [...chaosProgress].sort((a, b) => a.round - b.round)) {
    const streaming = live(chaos);
    const chaosText: string = chaos.chaos || "";
    const { targets, toPanel } = repliesFrom(chaos, PLACEHOLDERS.test(chaosText.trim()) ? "" : chaosText, members);
    for (const target of members) {
      const text = targets[target];
      const comment = byExpert.get(target)!;
      if (text) {
        comment.replies.push({
          id: `chaos-${chaos.round}-${target}`,
          author: "chaos",
          kind: "chaos",
          round: chaos.round,
          text,
          streaming,
          toPanel: false,
        });
      } else if (streaming) {
        comment.replying.push("chaos");
      }
    }
    if (toPanel) {
      panelReplies.push({
        id: `chaos-${chaos.round}-panel`,
        author: "chaos",
        kind: "chaos",
        round: chaos.round,
        text: toPanel,
        streaming,
        toPanel: true,
      });
    }
  }

  const synthesis = input.councilFinal
    ? { text: input.councilFinal, streaming: isLoading && !input.facilitatorDone }
    : null;
  const replyCount =
    comments.reduce((n, c) => n + c.replies.length, 0) + panelReplies.length;
  return {
    context: [],
    comments,
    panelReplies,
    synthesis,
    synthesisPending: !synthesis && isLoading && councilProgress.length > 0,
    replyCount,
  };
}

function buildSingle(input: ThreadInput): DebateThread {
  const { councilProgress, chaosProgress, isLoading } = input;
  const live = (step: any) => isLoading && step?.done !== true;
  const find = (step: string) => councilProgress.find((p) => p.step === step);

  const context: ContextNote[] = [];
  const frame = find("frame");
  if (frame && textOf(frame) && !/^\(Framing skipped/.test(textOf(frame))) {
    context.push({ key: "frame", author: "framer", title: "How the question was framed", text: textOf(frame), streaming: live(frame) });
  }
  const research = find("research_gather");
  if (research && textOf(research)) {
    context.push({ key: "research", author: "researcher", title: "Research brief", text: textOf(research), streaming: live(research), sourceIds: sourceIdsOf(research) });
  }

  const initial = find("initial");
  const revision = find("revision");
  const replies: ThreadReply[] = [];
  const replying: string[] = [];
  const chaos = chaosProgress.find((c) => c.round === 1);
  if (chaos?.chaos) {
    replies.push({ id: "chaos-1-analyst", author: "chaos", kind: "chaos", round: 1, text: chaos.chaos, streaming: live(chaos), toPanel: false, sourceIds: sourceIdsOf(chaos) });
  } else if (chaos && live(chaos)) {
    replying.push("chaos");
  }
  const factCheck = find("fact_check");
  if (factCheck && textOf(factCheck)) {
    replies.push({ id: "fact-check-analyst", author: "fact_checker", kind: "fact_check", round: 1, text: textOf(factCheck), streaming: live(factCheck), toPanel: false, sourceIds: sourceIdsOf(factCheck) });
  } else if (factCheck && live(factCheck)) {
    replying.push("fact_checker");
  }

  const analystSteps = councilProgress.filter((p) => p.expert === "analyst");
  const comments: ThreadComment[] =
    initial || isLoading
      ? [
          {
            expert: "analyst",
            original: textOf(initial),
            originalStreaming: !!initial && live(initial),
            revised: revision ? textOf(revision) : null,
            revisedStreaming: !!revision && live(revision),
            researching: !initial && isLoading,
            replies,
            replying,
            model: latestModel(analystSteps),
            openingLean: null,
            revisedLean: null,
            openingSourceIds: sourceIdsOf(initial),
            revisedSourceIds: sourceIdsOf(revision),
          },
        ]
      : [];

  // The analyst's revision is the final answer, so there is no separate synthesis.
  return { context, comments, panelReplies: [], synthesis: null, synthesisPending: false, replyCount: replies.length };
}

export function buildDebateThread(input: ThreadInput): DebateThread {
  return input.debateMode === "single" ? buildSingle(input) : buildMulti(input);
}
